/**
 * 받는 사람 가운데 «외부인» — 2026-09-22 사용자 결정으로 붙었다.
 *
 * ## 길이 둘이다
 *
 * ```
 * ① 주소를 그냥 적어 넣기     — 허용 도메인 «없다»(아무 주소나)
 * ② 주소록에서 골라 넣기       — contactsApi.list({ scope:'ALL' }) · 개인 ∪ 공유
 * ```
 * ⛔ 메일 갈래에만 있다. 메시지·결재는 «사내 사용자»라야 대화방·결재선이 성립한다.
 *
 * ## ⚠ 「막아 둔 주소」는 «보내지 말라»는 뜻이 «아니다» — 그래서 넣는 것을 막지 않는다
 *
 * `contactsApi.lookup` 의 `blocked` 는 `mail_address_rules(kind='BLOCK')` 이고, 서버 주석이
 * 적어 둔 그 표의 일은 **「그 주소에서 «오는» 메일을 스팸으로 분류」**다
 * (`contacts.routes.ts:502-507` · `resolveImportPlacement`). 내가 «그에게 보내는» 것을 금한 규칙이
 * 아니다 — 스팸을 많이 보내는 거래처 담당자에게 회의록을 보내야 할 수도 있다.
 *
 * 그래서 **넣게 하고, 배지로 계속 알린다.** 막으면 정당한 발송이 «까닭도 모르게» 막힌다. 빼는 것은
 * 사람이 정한다.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Mail, X, Users } from '../../icons';
import { Badge } from '../../components/shared/Badge';
import { Button } from '../../components/shared/Button';
import { Checkbox } from '../../components/shared/Checkbox';
import { InlineNote } from '../../components/shared/InlineNote';
import { contactsApi, type Contact } from '../../api/contacts';
import { tMinutes } from '../../lib/minutes-i18n';
import { looksLikeAddress, splitAddresses } from './minutes-send-address';

export interface ExternalRecipient {
  /** 늘 소문자로 담는다 — 같은 주소를 두 번 넣지 않으려는 것이다. */
  email: string;
  name?: string | null;
  /** 주소록에서 온 것인가 — 화면에 출처를 적는다. */
  fromContacts?: boolean;
}

export interface MinutesExternalRecipientsProps {
  value: readonly ExternalRecipient[];
  onChange: (next: ExternalRecipient[]) => void;
}

export function MinutesExternalRecipients({ value, onChange }: MinutesExternalRecipientsProps) {
  const [raw, setRaw] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const [bookOpen, setBookOpen] = useState(false);

  /** 막아 둔 주소 — 넣는 것을 막지 않고 «알리기만» 한다(위 docblock 참조). */
  const [blocked, setBlocked] = useState<Set<string>>(() => new Set());

  const emails = useMemo(() => value.map((v) => v.email), [value]);
  // 문자열 하나로 만들어 의존성에 쓴다 — 배열을 그대로 쓰면 렌더마다 새 참조라 effect 가 안 멈춘다.
  const emailsKey = useMemo(() => emails.join(','), [emails]);

  useEffect(() => {
    if (emailsKey === '') { setBlocked(new Set()); return; }
    let alive = true;
    void contactsApi.lookup(emailsKey.split(','))
      .then((r) => { if (alive) setBlocked(new Set(r.blocked.map((a) => a.toLowerCase()))); })
      .catch(() => { /* 알림용 배지일 뿐이다 — 못 물었다고 넣기를 막지 않는다. */ });
    return () => { alive = false; };
  }, [emailsKey]);

  const addAll = useCallback((items: ExternalRecipient[]) => {
    const have = new Set(value.map((v) => v.email));
    const next = [...value];
    for (const it of items) {
      const email = it.email.trim().toLowerCase();
      if (email === '' || have.has(email)) continue;
      have.add(email);
      next.push({ ...it, email });
    }
    onChange(next);
  }, [value, onChange]);

  const commitRaw = useCallback(() => {
    const parts = splitAddresses(raw);
    if (parts.length === 0) return;
    const bad = parts.find((p) => !looksLikeAddress(p));
    if (bad !== undefined) {
      setInputError(tMinutes('minutes.send.err_bad_email', { value: bad }));
      return;
    }
    setInputError(null);
    addAll(parts.map((email) => ({ email })));
    setRaw('');
  }, [raw, addAll]);

  const remove = useCallback((email: string) => {
    onChange(value.filter((v) => v.email !== email));
  }, [value, onChange]);

  return (
    <section className="msend-ext">
      <div className="msend-org-label">{tMinutes('minutes.send.external_label')}</div>

      {/* 담긴 외부 주소 */}
      {value.length > 0 && (
        <ul className="msend-chips">
          {value.map((r) => (
            <li key={r.email} className="msend-chip">
              <Mail size={12} aria-hidden />
              <span className="msend-chip-text">{r.name != null && r.name !== '' ? `${r.name} <${r.email}>` : r.email}</span>
              {r.fromContacts === true && <Badge variant="default" size="sm">{tMinutes('minutes.send.from_contacts')}</Badge>}
              {blocked.has(r.email) && <Badge variant="warning" size="sm">{tMinutes('minutes.send.blocked_badge')}</Badge>}
              <button
                type="button"
                className="msend-chip-x"
                onClick={() => remove(r.email)}
                aria-label={tMinutes('minutes.send.remove_recipient', { value: r.email })}
              >
                <X size={12} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}

      {[...blocked].length > 0 && (
        <InlineNote tone="warning">{tMinutes('minutes.send.blocked_hint')}</InlineNote>
      )}

      {/* ① 직접 적어 넣기 */}
      <div className="msend-ext-row">
        <input
          type="text"
          className="msend-ext-input"
          value={raw}
          placeholder={tMinutes('minutes.send.external_placeholder')}
          onChange={(e) => { setRaw(e.target.value); setInputError(null); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commitRaw(); }
          }}
          onBlur={commitRaw}
          aria-label={tMinutes('minutes.send.external_label')}
        />
        <Button variant="secondary" size="sm" onClick={commitRaw} disabled={raw.trim() === ''}>
          {tMinutes('minutes.send.external_add')}
        </Button>
      </div>
      {inputError !== null && <InlineNote tone="danger">{inputError}</InlineNote>}
      <p className="msend-payload-hint">{tMinutes('minutes.send.external_hint')}</p>

      {/* ② 주소록에서 골라 넣기 */}
      <Button variant="ghost" size="sm" iconLeft={<Users size={14} aria-hidden />} onClick={() => setBookOpen((v) => !v)}>
        {tMinutes('minutes.send.from_book')}
      </Button>
      {bookOpen && <ContactBook already={new Set(emails)} onPick={(items) => addAll(items)} />}
    </section>
  );
}

/**
 * 주소록 고르기 — 개인 ∪ 공유(`scope:'ALL'`).
 *
 * ★ 검색을 **서버에 묻는다**(`q`) — 주소록은 조직도와 달리 «한 번에 받아 둘 만한 크기»라는 보장이
 *   없다. 그래서 EntityPicker 의 「받아 둔 것 안에서 찾기」 규칙을 여기엔 적용하지 않는다.
 */
function ContactBook({ already, onPick }: { already: ReadonlySet<string>; onPick: (items: ExternalRecipient[]) => void }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<Contact[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [checked, setChecked] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    let alive = true;
    setFailed(false);
    const t = setTimeout(() => {
      void contactsApi.list({ scope: 'ALL', q: q.trim() === '' ? undefined : q.trim(), sort: 'name', limit: 50 })
        .then((r) => { if (alive) setRows(r.contacts); })
        .catch(() => { if (alive) { setFailed(true); setRows([]); } });
    }, 250); // 글자마다 부르지 않는다 — 분당 버킷에 걸린다.
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  const toggle = (email: string): void => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  const pick = (): void => {
    const map = new Map((rows ?? []).map((c) => [c.email.toLowerCase(), c]));
    onPick([...checked].map((email) => ({ email, name: map.get(email)?.name ?? null, fromContacts: true })));
    setChecked(new Set());
  };

  return (
    <div className="msend-book">
      <input
        type="text"
        className="msend-ext-input"
        value={q}
        placeholder={tMinutes('minutes.send.book_search')}
        onChange={(e) => setQ(e.target.value)}
        aria-label={tMinutes('minutes.send.book_search')}
      />
      {failed ? (
        <InlineNote tone="danger">{tMinutes('minutes.err.load_failed')}</InlineNote>
      ) : rows === null ? (
        <p className="minutes-muted">{tMinutes('minutes.list.loading')}</p>
      ) : rows.length === 0 ? (
        <p className="minutes-muted">{tMinutes('minutes.send.book_empty')}</p>
      ) : (
        <ul className="msend-book-list">
          {rows.map((c) => {
            const email = c.email.toLowerCase();
            const have = already.has(email);
            return (
              <li key={c.id}>
                <Checkbox
                  checked={have || checked.has(email)}
                  disabled={have}
                  onChange={() => toggle(email)}
                  label={
                    <span className="msend-book-row">
                      <span className="msend-book-name">{c.name ?? tMinutes('minutes.send.book_noname')}</span>
                      <span className="msend-book-mail">{c.email}</span>
                      <Badge variant="default" size="sm">
                        {c.scope === 'SHARED' ? tMinutes('minutes.send.book_shared') : tMinutes('minutes.send.book_personal')}
                      </Badge>
                    </span>
                  }
                />
              </li>
            );
          })}
        </ul>
      )}
      <Button variant="secondary" size="sm" disabled={checked.size === 0} onClick={pick}>
        {tMinutes('minutes.send.book_pick', { n: checked.size })}
      </Button>
    </div>
  );
}
